import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Box, Typography } from "@material-ui/core";
import { Colors, ScorecardConstants } from "constants/constants";
import { handleGetMissingDeliveries } from "../requests";
import { StationSelect } from "componentsNew/elements/SelectField";
import { SearchTypeSelect } from "components/elements/SearchTypeSelect";
import { MonthSelect } from "components/elements/MonthSelect";
import { WeekSelect } from "components/elements/WeekSelect";
import { useDateWeekSelector } from "hooks/useDateWeekSelector";
import { useStyles } from "./styles";
import { ScorecardOpenButton } from "components/buttons/ScorecardOpenButton";
import { ScorecardCompareButton } from "components/buttons/ScorecardCompareButton";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { useDispatch } from "react-redux";
import {
  ScorecardExternalSetDate,
  ScorecardSetSelectType,
  ScorecardSetStation,
} from "actions/scorecardActions";
import { DatePickWithArrow } from "components/elements/DatePickWithArrow";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const MissingDeliveries = ({ withModal = true, withCompare = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { userData } = useTypeSelectors((state) => state.auth);
  const { externalSettingActive, externalSettings, missingDeliveriesSettings } = useTypeSelectors(
    (state) => state.scoreCardSettings
  );
  const defaultStationCondition = userData?.station_spots.some(
    (el: { station: { title: string; id: number } }) => el.station.id === 1
  )
    ? 1
    : userData?.station_spots[0]?.station?.id;
  const [compareStation, setCompareStation] = useState(`${defaultStationCondition}`);
  const [requestData, setRequestData] = useState([]);

  const handleChangeStation = (data: string) =>
    !withCompare
      ? setCompareStation(data)
      : dispatch(
          ScorecardSetStation(data, ScorecardConstants.MISSING_DELIVERIES_SET_CURRENT_STATION)
        );
  const handleChangeSelectType = (select: string) =>
    dispatch(ScorecardSetSelectType(select, ScorecardConstants.MISSING_DELIVERIES_SET_SELECT_TYPE));
  const handleChangeWeek = (week: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...missingDeliveriesSettings?.date, week },
        ScorecardConstants.MISSING_DELIVERIES_SET_DATE
      )
    );
  const handleChangeMonth = (month: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...missingDeliveriesSettings?.date, month },
        ScorecardConstants.MISSING_DELIVERIES_SET_DATE
      )
    );
  const handleChangeYear = (year: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...missingDeliveriesSettings?.date, year },
        ScorecardConstants.MISSING_DELIVERIES_SET_DATE
      )
    );

  const handleChangeDayDate = (date: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...missingDeliveriesSettings?.date, day: date },
        ScorecardConstants.MISSING_DELIVERIES_SET_DATE
      )
    );

  const {
    week,
    month,
    year,
    date,
    selectType,
    onTypeChange,
    dateCondition,
    dateConditionMonth,
    onWeekReduce,
    onWeekIncrease,
    onMonthReduce,
    onMonthIncrease,
    onDateChange,
    onDateChangeByDay,
  } = useDateWeekSelector({
    defaultWeek: missingDeliveriesSettings?.date?.week,
    defaultMonth: missingDeliveriesSettings?.date?.month,
    defaultYear: missingDeliveriesSettings?.date?.year,
    defaultSelectType: missingDeliveriesSettings?.selectType,
    onSelectTypeSet: handleChangeSelectType,
    onWeekSet: handleChangeWeek,
    onMonthSet: handleChangeMonth,
    onYearSet: handleChangeYear,
    onDateSet: handleChangeDayDate,
  });

  const conditionLocalStation = !withCompare ? compareStation : missingDeliveriesSettings?.station;

  useEffect(() => {
    if (
      (conditionLocalStation && (month || week)) ||
      (externalSettings?.station && externalSettings?.selectType && externalSettings?.date)
    ) {
      const params = externalSettingActive
        ? {
            station: externalSettings?.station,
            ...(externalSettings?.selectType === "month" && {
              month: +externalSettings?.date?.month,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "week" && {
              week: +externalSettings?.date?.week,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "day" && { day: externalSettings?.date?.day }),
          }
        : {
            station: conditionLocalStation,
            year: year,
            ...(selectType === "month" && { year, month }),
            ...(selectType === "week" && { year, week }),
            ...(selectType === "day" && { day: date }),
          };
      handleGetMissingDeliveries(params, setRequestData);
    }
  }, [
    missingDeliveriesSettings?.station,
    year,
    month,
    week,
    date,
    selectType,
    externalSettingActive,
    externalSettings?.station,
    externalSettings?.selectType,
    externalSettings?.date,
    conditionLocalStation,
  ]);

  const labelsData = requestData.filter((el: { name: string; counter: number }) =>
    el.counter > 0 ? el.name : null
  );

  const labels = labelsData.map((el: { name: string }) => el.name).reverse();
  const counterArray = labelsData.map((el: { counter: number }) => el.counter);
  const total = counterArray.length > 0 ? counterArray.reduce((a, b) => a + b) : 0;

  const dataChart = {
    labels,
    datasets: [
      {
        label: "Counter",
        data: labelsData
          .reverse()
          .map((el: { counter: number; name: string }, index) =>
            el.name === labels[index] ? el.counter : null
          ),
        backgroundColor: labelsData.map((el: { counter: number }) =>
          el.counter > 0 ? Colors.PASTEL_RED : Colors.PASTEL_BLUE
        ),
        minBarLength: 3,
        barThickness: 15,
        borderRadius: 25,
      },
    ],
  };

  return (
    <Box className={classes.root}>
      {!externalSettingActive && (
        <Box className={classes.head}>
          <Box className={classes.box}>
            <StationSelect
              value={!withCompare ? compareStation : missingDeliveriesSettings?.station}
              onChange={handleChangeStation}
            />
            <SearchTypeSelect
              value={selectType}
              onChange={onTypeChange}
              options={[
                { id: "week", title: "Week" },
                { id: "month", title: "Month" },
                { id: "day", title: "Day" },
              ]}
            />
            {selectType === "month" && (
              <MonthSelect
                month={month}
                year={year}
                onNext={onMonthIncrease}
                onPrev={onMonthReduce}
                disabled={dateConditionMonth()}
              />
            )}
            {selectType === "week" && (
              <WeekSelect
                week={week}
                year={year}
                onNext={onWeekIncrease}
                onPrev={onWeekReduce}
                disabled={dateCondition()}
              />
            )}
            {selectType === "day" && (
              <DatePickWithArrow
                value={date}
                onChange={onDateChange}
                changeDay={onDateChangeByDay}
                displayWeek
              />
            )}
          </Box>
          <Box>
            {withCompare && (
              <ScorecardCompareButton
                children={<MissingDeliveries withModal={false} withCompare={false} />}
                children2={<MissingDeliveries withModal={false} withCompare={false} />}
              />
            )}
            {withModal && (
              <ScorecardOpenButton children={<MissingDeliveries withModal={false} />} />
            )}
          </Box>
        </Box>
      )}
      <Typography variant="body2" style={{ width: "100%", textAlign: "center" }}>
        Missing deliveries by DA, Total: <span style={{ fontWeight: 700 }}>{total}</span>
      </Typography>
      <Bar options={options} data={dataChart} />
    </Box>
  );
};

const options = {
  responsive: true,
  scale: {
    ticks: {
      precision: 0
    }
  },
  scales: {
    y: {
      title: {
        display: false,
        text: "Value",
      },
      min: 0,
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
    x: {
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top" as const,
      display: false,
    },
    title: {
      display: false,
      text: "Missing deliveries by DA",
    },
  },
};
